import React, { useState } from 'react';
import { formatAreaSqm } from 'utils';

const formatLabel = (val) => {
  if (!val) return '—';
  return val.replace(/_/g, ' ');
};

const JobDetailsPanel = ({
  job,
  trees = [],
  groups = [],
  selectedTreeId,
  selectedTreeIds = [],
  selectionMode,
  enums = {},
  onSelectTree,
  onToggleTreeSelection,
  onToggleSelectionMode,
  onTogglePlaceMode,
  placeMode,
  onSelectGroup,
  onEditTree,
  onSaveJob,
}) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', status: 'draft', notes: '' });

  if (!job) {
    return (
      <div className="empty-state">
        <p>No job selected</p>
      </div>
    );
  }

  const jobTrees = trees.filter((t) => t.job_id === job.job_id);
  const jobGroups = groups.filter((g) => g.job_id === job.job_id);
  const groupedTreeIds = new Set(jobGroups.flatMap((g) => g.tree_ids || []));
  const groupedCount = groupedTreeIds.size;
  const ungroupedCount = jobTrees.filter((t) => !groupedTreeIds.has(t.tree_id)).length;
  const selectedInJob = selectedTreeIds.filter((id) => jobTrees.some((t) => t.tree_id === id));

  const startEdit = () => {
    setForm({ name: job.name, status: job.status, notes: job.notes || '' });
    setEditing(true);
  };

  const handleSave = (e) => {
    e.preventDefault();
    onSaveJob?.({ ...form, job_id: job.job_id, site_id: job.site_id }, () => setEditing(false));
  };

  const handleTreeClick = (tree, e) => {
    if (selectionMode || e.shiftKey) {
      onToggleTreeSelection?.(tree);
    } else {
      onSelectTree?.(tree);
    }
  };

  const jobStatuses = enums.job_status || ['draft', 'in_progress', 'completed', 'archived'];

  return (
    <div className={selectionMode ? 'panel-selecting' : ''}>
      <div className="panel-section panel-section-header">
        {editing ? (
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label>Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {jobStatuses.map((s) => (
                  <option key={s} value={s}>{formatLabel(s)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="inline-actions">
              <button type="button" className="btn btn-secondary btn-sm" style={{ color: '#333', background: '#eee' }} onClick={() => setEditing(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary btn-sm">Save</button>
            </div>
          </form>
        ) : (
          <>
            <h2>{job.name}</h2>
            <div className="panel-meta-row">
              <span className="tag" style={{ background: '#e8f5e9', color: '#1a472a' }}>{formatLabel(job.status)}</span>
              <button type="button" className="btn btn-secondary btn-sm btn-edit" onClick={startEdit}>Edit</button>
            </div>
          </>
        )}
      </div>

      {!editing && job.notes && (
        <div className="panel-section">
          <h3>Notes</h3>
          <p className="empty-inline">{job.notes}</p>
        </div>
      )}

      {jobGroups.length > 0 ? (
        <div className="panel-section">
          <h3>Groups</h3>
          <p className="empty-inline" style={{ marginBottom: 8, fontSize: 12 }}>
            Area is the map polygon footprint around each group&apos;s trees.
          </p>
          {jobGroups.map((g) => (
            <div
              key={g.group_id}
              className="job-list-item job-list-item-touch"
              onClick={() => onSelectGroup?.(g)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onSelectGroup?.(g)}
            >
              <div className="list-item-headline">
                <h4>{g.name}</h4>
                <span className="list-item-meta">
                  {g.tree_ids?.length || 0} trees
                  {g.area_sqm != null ? ` · ${formatAreaSqm(g.area_sqm, { alwaysSqm: true })}` : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="panel-section">
          <h3>Groups</h3>
          <p className="empty-inline">No groups yet. Tap Select, choose trees, then Create group.</p>
        </div>
      )}

      <div className="panel-section">
        <div className="panel-section-title-row">
          <h3>Trees ({jobTrees.length})</h3>
          <div className="panel-inline-actions">
            <button
              type="button"
              className={`btn btn-sm btn-touch-inline ${selectionMode ? 'btn-primary' : 'btn-secondary'}`}
              style={selectionMode ? undefined : { color: '#333', background: '#eee' }}
              onClick={() => onToggleSelectionMode?.()}
            >
              {selectionMode
              ? (selectedInJob.length > 0 ? `Select ${selectedInJob.length}` : 'Done')
              : 'Select'}
            </button>
            <button
              type="button"
              className={`btn btn-sm btn-touch-inline ${placeMode ? 'btn-primary' : 'btn-secondary'}`}
              style={placeMode ? undefined : { color: '#333', background: '#eee' }}
              onClick={() => onTogglePlaceMode?.()}
            >
              {placeMode ? 'Cancel' : '+ Tree'}
            </button>
          </div>
        </div>
        {jobTrees.length > 0 && !selectionMode && !placeMode && (
          <p className="empty-inline" style={{ marginBottom: 8, fontSize: 12 }}>
            {groupedCount > 0
              ? `${groupedCount} in groups${ungroupedCount > 0 ? ` · ${ungroupedCount} not grouped` : ''}`
              : 'All trees are ungrouped'}
          </p>
        )}
        {selectionMode && (
          <p className="empty-inline selection-hint">
            {selectedInJob.length > 0
              ? `${selectedInJob.length} selected — pick 2+ to create a group`
              : 'Tap trees on the map or in this list'}
          </p>
        )}
        {placeMode && (
          <p className="empty-inline selection-hint">
            Tap the map to place a tree, or long-press anywhere on the map
          </p>
        )}
        {jobTrees.length === 0 && !placeMode && (
          <p className="empty-inline">No trees yet — tap + Tree or long-press the map.</p>
        )}
        {jobTrees.map((t) => {
          const isSelected = selectedTreeIds.includes(t.tree_id);
          const isFocused = !selectionMode && selectedTreeId === t.tree_id;
          const inGroup = groupedTreeIds.has(t.tree_id);
          return (
            <div
              key={t.tree_id}
              className={`job-list-item job-list-item-touch ${isFocused ? 'selected' : ''} ${isSelected ? 'multi-selected' : ''}`}
              onClick={(e) => handleTreeClick(t, e)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleTreeClick(t, e)}
            >
              <div className="job-list-item-row">
                {selectionMode && (
                  <span className={`tree-checkbox ${isSelected ? 'checked' : ''}`} aria-hidden="true" />
                )}
                <div className="job-list-item-body">
                  <div className="list-item-headline">
                    <h4>{t.common_name || `${t.genus} ${t.species}`}</h4>
                    <span className="list-item-meta">
                      {formatLabel(t.status)}
                      {inGroup ? ' · grouped' : ''}
                    </span>
                  </div>
                </div>
                {!selectionMode && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm btn-touch-inline"
                    style={{ color: '#333', background: '#eee' }}
                    onClick={(e) => { e.stopPropagation(); onEditTree?.(t); }}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JobDetailsPanel;
